import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Button,
  IconButton,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CardMedia,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  LocalOffer,
  LocationOn,
  Schedule,
  Refresh,
  SearchOff,
  CheckCircle,
  Redeem,
  ContentCopy,
  Image as ImageIcon
} from '@mui/icons-material';

// Import axiosInstance following the same pattern as TodoList
import axiosInstance from "../../../utils/axios/axiosInstance";
import { toast } from 'react-toastify';

// Import loading gif following the same pattern as TodoList
import loadingGif from '../../../assets/images/loading.gif';

const DealsList = () => {
  // Use ref to track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);

  // State declarations
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedCoupon, setCopiedCoupon] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [couponPopup, setCouponPopup] = useState({ open: false, deal: null });
  const [confirmationDialog, setConfirmationDialog] = useState({ open: false, deal: null });
  const [redeemingDeal, setRedeemingDeal] = useState(null);
  
  // Filter and Pagination states
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6); // 6 deals per page

  // Helper function to get category info
  const getCategoryInfo = useCallback((category) => {
    const categoryColors = {
      'Food': { color: '#FF6B6B', bgColor: '#ffebee' },
      'Fitness': { color: '#4ECDC4', bgColor: '#e0f2f1' },
      'Beverages': { color: '#45B7D1', bgColor: '#e3f2fd' },
      'Fashion': { color: '#96CEB4', bgColor: '#e8f5e9' },
      'Entertainment': { color: '#FFEAA7', bgColor: '#fffde7'},
      'Shopping': { color: '#ff9800', bgColor: '#fff3e0' },
      'Travel': { color: '#2196f3', bgColor: '#e3f2fd'},
      'Health': { color: '#f44336', bgColor: '#ffebee'}
    };
    
    return categoryColors[category] || { 
      color: '#ce55b9', 
      bgColor: '#f3e5f5', 
    };
  }, []);

  // Helper function to format date
  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'No expiry date';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  }, []);

  // Check if deal is expired
  const isDealExpired = useCallback((expiryDate) => {
    if (!expiryDate) return false;
    const today = new Date();
    const expiry = new Date(expiryDate);
    today.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);
    return expiry < today;
  }, []);

  // Handle image error
  const handleImageError = useCallback((dealId) => {
    setImageErrors(prev => ({
      ...prev,
      [dealId]: true
    }));
  }, []);

  // Copy coupon code to clipboard
  const copyCouponCode = useCallback(async (couponCode, dealId) => {
    try {
      await navigator.clipboard.writeText(couponCode);
      setCopiedCoupon(dealId);
      toast.success(`Coupon code "${couponCode}" copied to clipboard! 📋`, {
        position: "top-center",
        autoClose: 2000,
      });
      
      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopiedCoupon(null);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy coupon code:', err);
      toast.error('Failed to copy coupon code', {
        position: "top-center",
        autoClose: 3000,
      });
    }
  }, []);

  // Fetch deals from API using axiosInstance (same pattern as TodoList)
  const fetchDeals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('📤 Fetching deals...');

      const response = await axiosInstance.get(
        `${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/deals?claimed=0`
      );

      if (!isMountedRef.current) return;

      console.log('📥 Deals API Response:', response.data);

      if (response.data.status === 200) {
        const dealsData = response.data.data?.data || [];
        
        console.log('📥 Fetched deals:', dealsData);
        setDeals(dealsData);

        if (dealsData.length === 0) {
          toast.info('No deals available at the moment. Check back later! 🎯', {
            position: "top-center",
            autoClose: 3000,
          });
        }
      } else {
        throw new Error(response.data.message || 'Failed to fetch deals');
      }
    } catch (err) {
      console.error('❌ Error fetching deals:', err);

      let errorMessage = 'Failed to fetch deals';

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      if (isMountedRef.current) {
        setError(errorMessage);
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 5000,
        });
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Show coupon code popup
  const showCouponPopup = useCallback((deal) => {
    setCouponPopup({ open: true, deal });
  }, []);

  // Close coupon popup
  const closeCouponPopup = useCallback(() => {
    setCouponPopup({ open: false, deal: null });
  }, []);

  // Show confirmation dialog
  const showConfirmationDialog = useCallback((deal) => {
    setConfirmationDialog({ open: true, deal });
  }, []);

  // Close confirmation dialog
  const closeConfirmationDialog = useCallback(() => {
    setConfirmationDialog({ open: false, deal: null });
  }, []);

  // Redeem deal function
  const redeemDeal = useCallback(async (deal) => {
    if (!deal || !isMountedRef.current) return;

    try {
      setRedeemingDeal(deal.id);
      
      console.log('📤 Redeeming deal:', deal.id);

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/deals/${deal.id}/redeem`
      );

      if (!isMountedRef.current) return;

      console.log('📥 Redeem Deal Response:', response.data);

      if (response.data.status === 200) {
        // Remove the deal from the list
        setDeals(prev => prev.filter(d => d.id !== deal.id));
        
        // Close all dialogs
        setCouponPopup({ open: false, deal: null });
        setConfirmationDialog({ open: false, deal: null });
        
        toast.success('Deal redeemed successfully! 🎉', {
          position: "top-center",
          autoClose: 3000,
        });
      } else {
        throw new Error(response.data.message || 'Failed to redeem deal');
      }
    } catch (err) {
      console.error('❌ Error redeeming deal:', err);

      let errorMessage = 'Failed to redeem deal';

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      // Handle specific error for already redeemed deals
      if (err.response?.status === 409) {
        errorMessage = 'Deal already redeemed.';
      }

      if (isMountedRef.current) {
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 5000,
        });
      }
    } finally {
      if (isMountedRef.current) {
        setRedeemingDeal(null);
      }
    }
  }, []);

  // Get unique categories for filter
  const getUniqueCategories = useCallback(() => {
    const categories = [...new Set(deals.map(deal => deal.category))];
    return categories.sort();
  }, [deals]);

  // Filter deals
  const getFilteredDeals = useCallback(() => {
    let filteredDeals = [...deals];

    // Apply category filter
    if (categoryFilter !== 'all') {
      filteredDeals = filteredDeals.filter(deal => deal.category === categoryFilter);
    }

    // Sort by expiry date (earliest first) and then by discount (highest first)
    filteredDeals.sort((a, b) => {
      const dateA = new Date(a.expiry_date);
      const dateB = new Date(b.expiry_date);
      
      // If dates are the same, sort by discount (highest first)
      if (dateA.getTime() === dateB.getTime()) {
        return parseInt(b.discount) - parseInt(a.discount);
      }
      
      return dateA - dateB; // Earliest expiry first
    });

    return filteredDeals;
  }, [deals, categoryFilter]);

  // Get paginated deals
  const getPaginatedDeals = useCallback(() => {
    const filteredDeals = getFilteredDeals();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredDeals.slice(startIndex, endIndex);
  }, [getFilteredDeals, currentPage, itemsPerPage]);

  // Calculate total pages
  const totalPages = Math.ceil(getFilteredDeals().length / itemsPerPage);

  // Handle page change
  const handlePageChange = useCallback((event, value) => {
    setCurrentPage(value);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Handle category filter change
  const handleCategoryFilterChange = useCallback((event) => {
    setCategoryFilter(event.target.value);
    setCurrentPage(1); // Reset to first page when filter changes
  }, []);

  // Fetch deals on component mount
  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  // Reset current page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [categoryFilter]);

  // Cleanup effect
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Early return for loading state (same pattern as TodoList)
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, fontFamily: '"Saira", sans-serif' }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh'
        }}>
          {loadingGif ? (
            <img
              src={loadingGif}
              alt="Loading..."
              style={{ width: '80px' }}
            />
          ) : (
            <CircularProgress size={60} />
          )}
          <Typography variant="body1" sx={{ mt: 2, fontFamily: '"Saira", sans-serif' }}>
            Loading amazing deals...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Early return for error state
  if (error && !deals.length) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, fontFamily: '"Saira", sans-serif' }}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography sx={{ fontFamily: '"Saira", sans-serif' }}>
              <strong>Error!</strong> {error}
            </Typography>
          </Alert>
          <Button
            variant="contained"
            onClick={fetchDeals}
            startIcon={<Refresh />}
            sx={{
              mt: 2,
              fontFamily: '"Saira", sans-serif',
              background: '#ce55b9',
              '&:hover': {
                background: '#b8479a',
              }
            }}
          >
            Try Again
          </Button>
        </Box>
      </Container>
    );
  }

  const filteredDeals = getFilteredDeals();
  const paginatedDeals = getPaginatedDeals();
  const uniqueCategories = getUniqueCategories();

  return (
    <Grid container spacing={2}>
      <div className="container">
        <div className="css-11ezll3-MuiToolbar-root breadcrumbs">
          <div className="top-sec">
            <div className="card custom-card">
              <div className="card-body">
                {/* Header Section - Refresh button removed */}
                <div className="d-flex align-items-center justify-content-between flex-wrap pb-1">
                  <div className="d-flex align-items-center">
                    <h3 className="mb-2">Deals List</h3>
                  </div>
                </div>

                {/* Description */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" sx={{
                    color: '#666',
                    fontFamily: '"Saira", sans-serif',
                    lineHeight: 1.6
                  }}>
                    Discover amazing opportunities and exclusive offers near you!
                  </Typography>
                </Box>

                {/* Filter Section */}
                

                {/* Error Alert */}
                {error && deals.length > 0 && (
                  <Alert
                    severity="error"
                    sx={{
                      mb: 2,
                      fontFamily: '"Saira", sans-serif'
                    }}
                    onClose={() => setError(null)}
                  >
                    <Typography sx={{ fontFamily: '"Saira", sans-serif' }}>
                      {error}
                    </Typography>
                  </Alert>
                )}

                {/* Deals Grid */}
                <Grid container spacing={3}>
                  {paginatedDeals.length > 0 ? (
                    paginatedDeals.map((deal) => {
                      const categoryInfo = getCategoryInfo(deal.category);
                      const isExpired = isDealExpired(deal.expiry_date);
                      const hasImageError = imageErrors[deal.id];

                      return (
                        <Grid item xs={12} sm={6} lg={4} key={deal.id}>
                          <Card
                            sx={{
                              height: '100%',
                              position: 'relative',
                              transition: 'all 0.3s ease',
                              opacity: isExpired ? 0.6 : 1,
                              background: isExpired ? '#f5f5f5' : 'white',
                              border: isExpired ? '1px solid #e0e0e0' : '1px solid #e0e0e0',
                              borderRadius: 3,
                              overflow: 'hidden',
                              fontFamily: '"Saira", sans-serif',
                              '&:hover': {
                                transform: isExpired ? 'none' : 'scale(1.02)',
                                boxShadow: isExpired ? 'none' : '0 8px 25px rgba(0,0,0,0.15)',
                              }
                            }}
                          >
                            {/* Discount Badge */}
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 16,
                                left: 16,
                                background: isExpired ? '#bdbdbd' : 'linear-gradient(135deg, #FF6B6B, #ee5a52)',
                                color: 'white',
                                px: 2,
                                py: 1,
                                borderRadius: '20px',
                                fontSize: '14px',
                                fontWeight: 700,
                                fontFamily: '"Saira", sans-serif',
                                zIndex: 2,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                              }}
                            >
                              {deal.discount}% OFF
                            </Box>

                      

                            {/* Image Section */}
                            <Box sx={{ height: 170, position: 'relative' }}>
                              {deal.image && !hasImageError ? (
                                <CardMedia
                                  component="img"
                                  height="170px"
                                  image={deal.image}
                                  alt={deal.title}
                                  onError={() => handleImageError(deal.id)}
                                  sx={{
                                    objectFit: 'cover',
                                    width: '100%',
                                    height: '170px',
                                    filter: isExpired ? 'grayscale(100%)' : 'none'
                                  }}
                                />
                              ) : (
                                // Fallback when no image or image failed to load
                                <Box sx={{
                                  height: '100%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: `linear-gradient(135deg, ${categoryInfo.bgColor}, ${categoryInfo.bgColor}dd)`,
                                  color: categoryInfo.color
                                }}>
                                  <Box sx={{ fontSize: '36px', mb: 1 }}>
                                    {categoryInfo.icon}
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.7 }}>
                                    <ImageIcon sx={{ fontSize: '14px' }} />
                                    <Typography variant="caption" sx={{ 
                                      fontSize: '10px',
                                      fontFamily: '"Saira", sans-serif'
                                    }}>
                                      {hasImageError ? 'Image unavailable' : 'No image'}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                              
                              {isExpired && (
                                <Box sx={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  background: 'rgba(189, 189, 189, 0.9)',
                                  color: 'white',
                                  px: 3,
                                  py: 1,
                                  borderRadius: 2,
                                  fontSize: '14px',
                                  fontWeight: 600,
                                  fontFamily: '"Saira", sans-serif'
                                }}>
                                  EXPIRED
                                </Box>
                              )}
                            </Box>

                            <CardContent sx={{ p: 3 }}>
                              {/* Deal Title */}
                              <Typography
                                variant="h6"
                                component="h3"
                                sx={{
                                  color: isExpired ? 'text.secondary' : 'text.primary',
                                  textDecoration: isExpired ? 'line-through' : 'none',
                                  fontWeight: 600,
                                  lineHeight: 1.3,
                                  fontFamily: '"Saira", sans-serif',
                                  mb: 2,
                                  minHeight: 48 // Consistent height
                                }}
                              >
                                {deal.title}
                              </Typography>

                              {/* NOTE: Coupon Code Section REMOVED - will only show in popup */}

                              {/* Location */}
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 1,
                                p: 1.5,
                                backgroundColor: 'rgba(0,0,0,0.03)',
                                borderRadius: 2
                              }}>
                                <LocationOn sx={{
                                  fontSize: 18,
                                  mr: 1.5,
                                  color: '#1976d2'
                                }} />
                                <Typography variant="body2" sx={{ 
                                  fontWeight: 500, 
                                  fontFamily: '"Saira", sans-serif',
                                  color: isExpired ? 'text.secondary' : 'text.primary'
                                }}>
                                  {deal.location}
                                </Typography>
                              </Box>

                              {/* Expiry Date */}
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 2,
                                p: 1.5,
                                backgroundColor: isExpired ? 'rgba(244, 67, 54, 0.1)' : 'rgba(255, 107, 107, 0.1)',
                                borderRadius: 2
                              }}>
                                <Schedule sx={{
                                  fontSize: 18,
                                  mr: 1.5,
                                  color: isExpired ? '#f44336' : '#FF6B6B'
                                }} />
                                <Typography variant="body2" sx={{
                                  color: isExpired ? '#f44336' : '#FF6B6B',
                                  fontWeight: 600,
                                  fontFamily: '"Saira", sans-serif'
                                }}>
                                  {isExpired ? 'Expired: ' : 'Expires: '}{formatDate(deal.expiry_date)}
                                </Typography>
                              </Box>

                              {/* Actions */}
                              <Box sx={{
                                display: 'flex',
                                gap: 1,
                                justifyContent: 'center',
                                pt: 1
                              }}>
                                {/* Redeem Button */}
                                <Button
                                  variant="contained"
                                  disabled={isExpired || loading}
                                  onClick={() => showCouponPopup(deal)}
                                  startIcon={<Redeem />}
                                  sx={{
                                    backgroundColor: isExpired ? '#bdbdbd' : '#4caf50',
                                    color: 'white',
                                    fontFamily: '"Saira", sans-serif',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    py: 1.5,
                                    px: 3,
                                    borderRadius: 2,
                                    width: '100%',
                                    '&:hover': {
                                      backgroundColor: isExpired ? '#bdbdbd' : '#388e3c',
                                    },
                                    '&:disabled': {
                                      backgroundColor: '#bdbdbd',
                                      color: 'white'
                                    }
                                  }}
                                >
                                  {isExpired ? 'Expired' : 'Get Coupon Code'}
                                </Button>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })
                  ) : (
                    // No deals found message
                    <Grid item xs={12}>
                      <Box sx={{
                        textAlign: 'center',
                        py: 6,
                        px: 4,
                        backgroundColor: '#fafafa',
                        borderRadius: 2,
                        border: '1px solid #e0e0e0'
                      }}>
                        <SearchOff sx={{
                          fontSize: 48,
                          color: '#bdbdbd',
                          mb: 2
                        }} />
                        <Typography variant="h6" sx={{
                          mb: 1,
                          fontWeight: 600,
                          fontFamily: '"Saira", sans-serif',
                          color: '#424242'
                        }}>
                          No deals found
                        </Typography>
                        <Typography variant="body2" sx={{
                          mb: 4,
                          color: '#757575',
                          fontFamily: '"Saira", sans-serif',
                          maxWidth: 400,
                          mx: 'auto',
                          lineHeight: 1.5
                        }}>
                          {categoryFilter !== 'all' ?
                            'No deals match your current filter criteria. Try adjusting your filters or check back later.' :
                            'No deals available at the moment. Check back later for amazing offers!'
                          }
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                          {categoryFilter !== 'all' && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                setCategoryFilter('all');
                                setCurrentPage(1);
                              }}
                              sx={{
                                borderColor: '#e0e0e0',
                                color: '#757575',
                                fontFamily: '"Saira", sans-serif',
                                fontWeight: 500,
                                textTransform: 'none',
                                px: 2,
                                minWidth: 120,
                                borderRadius: 1,
                                '&:hover': {
                                  borderColor: '#bdbdbd',
                                  backgroundColor: '#f5f5f5',
                                  color: '#424242'
                                }
                              }}
                            >
                              Clear Filters
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>

                {/* Pagination Info */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 3,
                  mb: 2
                }}>
                  <Typography variant="body2" sx={{
                    color: '#6c757d',
                    fontFamily: '"Saira", sans-serif',
                    textAlign: 'center'
                  }}>
                    Showing {paginatedDeals.length} of {filteredDeals.length} deals
                    {categoryFilter !== 'all' && (
                      <span> • Category: {getCategoryInfo(categoryFilter).icon} {categoryFilter}</span>
                    )}
                    {filteredDeals.length > 0 && <span> • Sorted by expiry date</span>}
                  </Typography>
                </Box>

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: 4
                  }}>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      size="large"
                      sx={{
                        '& .MuiPaginationItem-root': {
                          fontFamily: '"Saira", sans-serif',
                          fontWeight: 600,
                          '&.Mui-selected': {
                            backgroundColor: '#ce55b9',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: '#b8479a',
                            }
                          }
                        }
                      }}
                    />
                  </Box>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Coupon Code Popup Dialog */}
        <Dialog
          open={couponPopup.open}
          onClose={closeCouponPopup}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              p: 1
            }
          }}
        >
          <DialogTitle sx={{
            textAlign: 'center',
            fontFamily: '"Saira", sans-serif',
            fontWeight: 700,
            fontSize: '1.5rem',
            color: '#4caf50',
            pb: 1
          }}>
            🎉 Coupon Code Ready!
          </DialogTitle>
          
          <DialogContent sx={{ textAlign: 'center', px: 3 }}>
            {couponPopup.deal && (
              <>
                <Typography variant="h6" sx={{
                  fontFamily: '"Saira", sans-serif',
                  fontWeight: 600,
                  mb: 2,
                  color: '#333'
                }}>
                  {couponPopup.deal.title}
                </Typography>
                
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  mb: 3,
                  p: 3,
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  borderRadius: 3,
                  border: '2px dashed #4caf50'
                }}>
                  <LocalOffer sx={{ fontSize: 24, color: '#4caf50' }} />
                  <Typography variant="h4" sx={{
                    fontWeight: 900,
                    fontFamily: '"Saira", sans-serif',
                    color: '#4caf50',
                    letterSpacing: '2px'
                  }}>
                    {couponPopup.deal.coupon_code}
                  </Typography>
                  <IconButton
                    onClick={() => copyCouponCode(couponPopup.deal.coupon_code, couponPopup.deal.id)}
                    sx={{
                      color: copiedCoupon === couponPopup.deal.id ? '#4caf50' : '#666',
                      '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.1)' }
                    }}
                  >
                  {/*}  {copiedCoupon === couponPopup.deal.id ? 
                      <CheckCircle sx={{ fontSize: 24 }} /> : 
                      <ContentCopy sx={{ fontSize: 24 }} />
                    }*/}
                  </IconButton>
                </Box>

                <Typography variant="body2" sx={{
                  color: '#666',
                  fontFamily: '"Saira", sans-serif',
                  mb: 1
                }}>
                  📍 {couponPopup.deal.location}
                </Typography>
                
                <Typography variant="body2" sx={{
                  color: '#ff6b6b',
                  fontWeight: 600,
                  fontFamily: '"Saira", sans-serif'
                }}>
                  ⏰ Expires: {formatDate(couponPopup.deal.expiry_date)}
                </Typography>
              </>
            )}
          </DialogContent>
          
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button
              onClick={() => showConfirmationDialog(couponPopup.deal)}
              variant="contained"
              disabled={redeemingDeal === couponPopup.deal?.id}
              startIcon={redeemingDeal === couponPopup.deal?.id ? 
                <CircularProgress size={18} color="inherit" /> : 
                <Redeem />
              }
              sx={{
                backgroundColor: '#4caf50',
                color: 'white',
                fontFamily: '"Saira", sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                px: 4,
                py: 1.5,
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: '#388e3c'
                }
              }}
            >
              {redeemingDeal === couponPopup.deal?.id ? 'Redeeming...' : 'Redeem'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmationDialog.open}
          onClose={closeConfirmationDialog}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              p: 1
            }
          }}
        >
          <DialogTitle sx={{
            textAlign: 'center',
            fontFamily: '"Saira", sans-serif',
            fontWeight: 700,
            fontSize: '1.3rem',
            color: '#ff9800',
            pb: 1
          }}>
            ⚠️ Confirm Redemption
          </DialogTitle>
          
          <DialogContent sx={{ textAlign: 'center', px: 3 }}>
            <Typography variant="body1" sx={{
              fontFamily: '"Saira", sans-serif',
              color: '#333',
              mb: 2,
              lineHeight: 1.5
            }}>
              Are you sure you want to redeem this deal?
            </Typography>
            
            {confirmationDialog.deal && (
              <Typography variant="body2" sx={{
                fontFamily: '"Saira", sans-serif',
                color: '#666',
                fontStyle: 'italic'
              }}>
                "{confirmationDialog.deal.title}"
              </Typography>
            )}
            
            <Typography variant="body2" sx={{
              fontFamily: '"Saira", sans-serif',
              color: '#f44336',
              mt: 2,
              fontWeight: 500
            }}>
              This action cannot be undone and the deal will be removed from your list.
            </Typography>
          </DialogContent>
          
          <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
            <Button
              onClick={closeConfirmationDialog}
              variant="outlined"
              sx={{
                borderColor: '#ccc',
                color: '#666',
                fontFamily: '"Saira", sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                px: 3,
                py: 1,
                borderRadius: 2,
                '&:hover': {
                  borderColor: '#999',
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              Cancel
            </Button>
            
            <Button
              onClick={() => redeemDeal(confirmationDialog.deal)}
              variant="contained"
              disabled={redeemingDeal === confirmationDialog.deal?.id}
              startIcon={redeemingDeal === confirmationDialog.deal?.id ? 
                <CircularProgress size={18} color="inherit" /> : 
                <CheckCircle />
              }
              sx={{
                backgroundColor: '#f44336',
                color: 'white',
                fontFamily: '"Saira", sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                px: 3,
                py: 1,
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: '#d32f2f'
                }
              }}
            >
              {redeemingDeal === confirmationDialog.deal?.id ? 'Redeeming...' : 'Yes, Redeem'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </Grid>
  );
};

export default DealsList;